import { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
} from '@acb_devel/design-system';
import { Button } from '@acb_devel/design-system';
import { FileUpload } from '@acb_devel/design-system';
import { ToastRoot } from '@acb_devel/design-system';
import { Alert } from '@acb_devel/design-system';
import { Text } from '@acb_devel/design-system';
import { updateProfile, uploadProfilePicture } from '@services/profiles';
import { useUserContext } from '@contexts/UserContext';
import { useToastContext } from '@acb_devel/design-system';

import styles from './EditAvatarModal.module.scss';

export function EditAvatarModal({
  isOpen,
  closeModal,
}: {
  isOpen: boolean;
  closeModal: () => void;
}) {
  const [selectedFile, setSelectedFile] = useState<string[]>([]);
  const [profilePicturePath, setProfilePicturePath] = useState<string>('');
  const [profilePictureUrl, setProfilePictureUrl] = useState<string>('');
  const { user } = useUserContext();
  const { addToast } = useToastContext();

  const onChangeProfilePicture = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    uploadProfilePicture(file)
      .then((response) => {
        setProfilePicturePath(response.storagePath);
        setProfilePictureUrl(response.storageUrl);
        setSelectedFile([response.storageUrl]);
      })
      .catch((error) => {
        console.error('Error uploading profile picture', error);
        addToast(
          <ToastRoot>
            <Alert
              variant="error"
              direction="column"
              title={`Error al subir la foto de perfil`}
              text="Ha ocurrido un error al subir tu foto de perfil. Asegúrate que el fichero cumpla el formato y tamaño permitido (5 MB)."
            />
          </ToastRoot>
        );
      });
  };

  function updateProfilePicture() {
    if (!user?.profile?.id) {
      return;
    }

    user.profile.profilePicturePath = profilePicturePath;
    user.profile.profilePictureFullUrl = profilePictureUrl;

    updateProfile(user.profile.id, {
      ...user.profile,
    })
      .then(() => {
        addToast(
          <ToastRoot>
            <Alert variant="success" title="Foto de perfil actualizada" />
          </ToastRoot>
        );
        closeModal();
      })
      .catch((error) => {
        addToast(
          <ToastRoot>
            <Alert
              variant="error"
              title="Error al actualizar la foto de perfil"
            />
          </ToastRoot>
        );

        console.error('Error updating profile picture', error);
      });
  }

  useEffect(() => {
    if (
      user?.profile?.profilePictureFullUrl &&
      user?.profile?.profilePicturePath
    ) {
      setSelectedFile([user.profile.profilePictureFullUrl]);
      setProfilePicturePath(user.profile.profilePicturePath);
      setProfilePictureUrl(user.profile.profilePictureFullUrl);
    }
  }, [user, isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={closeModal} size="md">
      <ModalHeader closeModal={closeModal}>Cambiar foto de perfil</ModalHeader>
      <ModalBody>
        <Label htmlFor="fr">Tu foto de perfil</Label>
        <br />
        <Text variant="body-2" className={styles.modal__clarificationText}>
          Por favor, sube una foto tipo carnet, en la que tu rostro sea
          claramente reconocible. Asegúrate de que tenga buena calidad y esté
          bien iluminada.
        </Text>
        <FileUpload
          id="profilePicture"
          label=""
          clarificationText="Adjuntar la imagen en formato jpg o png y con un tamaño máximo de 5MB"
          accept="image/*"
          onChange={onChangeProfilePicture}
          onDelete={() => {
            setProfilePicturePath('');
            setProfilePictureUrl('');
          }}
          buttonText="Adjuntar foto"
          buttonUpperCase={false}
          value={selectedFile}
          setValue={setSelectedFile}
        />
      </ModalBody>
      <ModalFooter>
        <div className={styles.modal__footer}>
          <Button variant="outline" onClick={closeModal}>
            Cancelar
          </Button>
          <Button variant="solid" onClick={updateProfilePicture}>
            Guardar
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}
